import _ from 'lodash';
import type { z } from 'zod';
import { SearchStringSchema } from '../common/schemas';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { LegacyTopicAttributes } from './constants';
import { DBLegacyTopicSchema, LegacyTopicSchema } from './schemas';

type LegacyTopic = z.infer<typeof LegacyTopicSchema>;

export const LegacyTopicCodecConfig: TransformerConfig<
  typeof LegacyTopicSchema,
  typeof DBLegacyTopicSchema
> = {
  name: 'LegacyTopic',
  clientSchema: LegacyTopicSchema,
  dbSchema: DBLegacyTopicSchema,
  transformers: [
    // exams: [examId] ↔ exams: { examId: {} }
    createTransformer<typeof LegacyTopicSchema, typeof DBLegacyTopicSchema>({
      encoder: {
        sourceFields: ['exams'] as const,
        encode: ({ exams }) => ({
          exams: Object.fromEntries((exams as LegacyTopic['exams']).map(val => [`${val}`, {}])),
        }),
      },
      decoder: {
        sourceFields: ['exams'] as const,
        decode: ({ exams }) => ({
          exams: Object.keys(exams ?? {}),
        }),
      },
    }),

    // Handle search string updates
    createTransformer<typeof LegacyTopicSchema, typeof DBLegacyTopicSchema>({
      encoder: {
        sourceFields: LegacyTopicAttributes.Search,
        dependencies: LegacyTopicAttributes.Search,
        encode: topic => {
          let searchStr = '';
          LegacyTopicAttributes.Search.forEach(attr => {
            const val = topic[attr];
            searchStr += SearchStringSchema.parse(val);
          });
          return { searchStr, ..._.pick(topic, LegacyTopicAttributes.Search) };
        },
      },
    }),
  ],
};

export const encodeLegacyTopicToDb = createEncodeToDbFn(LegacyTopicCodecConfig);
export const decodeLegacyTopicFromDb = createDecodeFromDbFn(LegacyTopicCodecConfig);
